import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import SimplePasswordChangeModal from './SimplePasswordChangeModal';
import ForgotUsernameModal from './ForgotUsernameModal';
import ForgotPasswordModal from './ForgotPasswordModal';
// Note: SimpleLogin is rendered inside AuthContainer/AuthCard from App.js
// So we don't need AuthPage wrapper here, just render content directly

// Teal color constant for Enroll Now button (matches existing #1F6F78)
const TEAL_SECONDARY = '#1F6F78';

const LoginContainer = styled.div`
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: var(--background-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: color 0.2s ease;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-secondary);
    }
  }
`;

const Button = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.875rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const EnrollNowButton = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.875rem 1.5rem;
  background-color: ${TEAL_SECONDARY};
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

// Soft Alert Banner for errors/session messages
const AlertBanner = styled(motion.div)`
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: var(--card-radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const AlertIcon = styled.div`
  color: rgba(220, 53, 69, 0.9);
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const AlertText = styled.div`
  color: rgba(220, 53, 69, 0.95);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;
`;

const GreetingWrapper = styled.h2`
  text-align: center;
  width: 100%;
  margin-bottom: 1.25rem;
  margin-top: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  font-family: Arial, "Segoe UI", "Noto Sans Arabic", "Arabic UI Text", sans-serif;
  
  .arabic-text {
    display: inline-block;
    direction: rtl;
    unicode-bidi: bidi-override;
    text-align: center;
    width: 100%;
  }
`;

const ForgotUsernameLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  
  button {
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    font-size: inherit;
    transition: color 0.2s ease, text-decoration 0.2s ease;
    padding: 0.25rem 0.5rem;
    white-space: nowrap;
    
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--primary-color);
        text-decoration: underline;
      }
    }
    
    &:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
  
  .separator {
    color: var(--text-muted);
    opacity: 0.5;
    user-select: none;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8125rem;
    gap: 0.375rem;
    
    button {
      padding: 0.25rem 0.375rem;
      font-size: 0.8125rem;
    }
  }
`;

const HelpLink = styled.div`
  text-align: center;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
  
  button {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: inherit;
    transition: color 0.2s ease, text-decoration 0.2s ease;
    padding: 0.25rem 0.5rem;
    
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--text-secondary);
        text-decoration: underline;
      }
    }
    
    &:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid var(--text-inverse);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function SimpleLogin() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showForgotUsernameModal, setShowForgotUsernameModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showLoggedOut, setShowLoggedOut] = useState(false);
  
  const { login, markPasswordChanged, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if user is already authenticated
  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) {
      return;
    }
    
    // Check if user is authenticated (either from context or localStorage)
    const token = localStorage.getItem('faithinsight_token');
    const savedUser = localStorage.getItem('faithinsight_user');
    const sessionExpired = localStorage.getItem('faithinsight_session_expired');
    
    // Don't redirect if session expired or logged out
    if (sessionExpired === 'true') {
      return;
    }
    
    // If we have a token and user data, or if auth context says we're authenticated
    if ((token && savedUser) || (isAuthenticated && user)) {
      // Get redirect URL from query parameters or localStorage
      const urlParams = new URLSearchParams(location.search);
      const redirectFromQuery = urlParams.get('redirect');
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
      
      // Store redirect in localStorage if it's in the URL
      if (redirectFromQuery && !redirectFromStorage) {
        localStorage.setItem('faithinsight_login_redirect', redirectFromQuery);
      }
      
      const redirectUrl = redirectFromQuery || redirectFromStorage || '/dashboard';
      
      // Clear redirect from localStorage if it was used
      if (redirectFromStorage && redirectFromQuery) {
        localStorage.removeItem('faithinsight_login_redirect');
      }
      
      // Navigate to the redirect URL
      if (redirectUrl === '/catalog') {
        window.location.href = '/portal/catalog?redirect=/catalog';
      } else {
        navigate(redirectUrl, { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, user, location.search, navigate]);

  // Check for session expired or logged out flags on component mount
  useEffect(() => {
    const loggedOut = localStorage.getItem('faithinsight_logged_out');
    const sessionExpired = localStorage.getItem('faithinsight_session_expired');
    
    if (loggedOut === 'true') {
      setShowLoggedOut(true);
      // Clear the flag since we're showing the message
      localStorage.removeItem('faithinsight_logged_out');
    } else if (sessionExpired === 'true') {
      setShowSessionExpired(true);
      // Clear the flag since we're showing the message
      localStorage.removeItem('faithinsight_session_expired');
    }
  }, []);

  // Preserve redirect parameter in localStorage when component mounts or URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');
    const redirectFromState = location.state?.from?.pathname;
    
    // Store redirect in localStorage so it persists through errors and password reset flows
    const redirectToStore = redirectFromQuery || redirectFromState;
    if (redirectToStore) {
      localStorage.setItem('faithinsight_login_redirect', redirectToStore);
    }
  }, [location.search, location.state]);

  const getRedirectUrl = (clearAfterUse = false) => {
    // Check for redirect in query parameters first
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');
    
    // Then check for redirect in location state
    const redirectFromState = location.state?.from?.pathname;
    
    // Then check localStorage (preserved from previous visits)
    const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
    
    // Prioritize query parameter, then state, then localStorage, then default to dashboard
    const redirectUrl = redirectFromQuery || redirectFromState || redirectFromStorage || '/dashboard';
    
    // Only clear from localStorage after successful login (when clearAfterUse is true)
    if (clearAfterUse && redirectFromStorage) {
      localStorage.removeItem('faithinsight_login_redirect');
    }
    
    return redirectUrl;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Clear logged out and session expired messages when user attempts to log in
    setShowLoggedOut(false);
    setShowSessionExpired(false);

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      if (result.requiresPasswordChange) {
        setShowPasswordChangeModal(true);
      } else {
        // Get redirect URL immediately
        const redirectUrl = getRedirectUrl(false);
        
        // Navigate immediately before any re-renders can happen
        // Use window.location for catalog to ensure immediate navigation without showing dashboard
        if (redirectUrl === '/catalog') {
          // Navigate to catalog with redirect parameter (full page reload to prevent dashboard flash)
          window.location.href = '/portal/catalog?redirect=/catalog';
        } else {
          // Clear redirect from localStorage after successful login for non-catalog redirects
          const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
          if (redirectFromStorage) {
            localStorage.removeItem('faithinsight_login_redirect');
          }
          navigate(redirectUrl, { replace: true });
        }
      }
    } else {
      setError(result.error || 'Login failed. Please try again.');
      // Don't clear redirect on error - keep it for next login attempt
    }
    
    setLoading(false);
  };

  const handlePasswordChangeSuccess = () => {
    markPasswordChanged();
    setShowPasswordChangeModal(false);
    // Get redirect URL immediately
    const redirectUrl = getRedirectUrl(false);
    
    // Navigate immediately before any re-renders can happen
    // Use window.location for catalog to ensure immediate navigation
    if (redirectUrl === '/catalog') {
      // Navigate to catalog with redirect parameter
      window.location.href = '/portal/catalog?redirect=/catalog';
    } else {
      // Clear redirect from localStorage after successful login for non-catalog redirects
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
      if (redirectFromStorage) {
        localStorage.removeItem('faithinsight_login_redirect');
      }
      navigate(redirectUrl, { replace: true });
    }
  };

  const handleNeedHelp = () => {
    // Open email client to admin
    window.open('mailto:admin@faithinsight.org');
  };

  return (
    <>
      <LoginContainer>
        <GreetingWrapper>
          <span className="arabic-text">السلام عليكم</span>
        </GreetingWrapper>
        
        <Form onSubmit={handleSubmit}>
            {/* All error messages displayed above email input */}
            {showLoggedOut && (
              <AlertBanner
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                role="alert"
              >
                <AlertIcon>
                  <FiAlertCircle />
                </AlertIcon>
                <AlertText>
                  You have been logged out. Please sign in again to continue.
                </AlertText>
              </AlertBanner>
            )}
            
            {showSessionExpired && (
              <AlertBanner
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                role="alert"
              >
                <AlertIcon>
                  <FiAlertCircle />
                </AlertIcon>
                <AlertText>
                  Your session has expired. Please sign in again to continue.
                </AlertText>
              </AlertBanner>
            )}

            {error && (
              <AlertBanner
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                role="alert"
              >
                <AlertIcon>
                  <FiAlertCircle />
                </AlertIcon>
                <AlertText>
                  {error}
                </AlertText>
              </AlertBanner>
            )}

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
        </Form>

        <EnrollNowButton className="enroll-now-button" onClick={() => {
            // Preserve redirect parameter when navigating to signup
            const urlParams = new URLSearchParams(location.search);
            const redirectParam = urlParams.get('redirect');
            const redirectState = location.state?.from?.pathname;
            
            navigate('/signup', {
              state: {
                from: redirectParam || redirectState ? { pathname: redirectParam || redirectState } : { pathname: '/catalog' }
              },
              search: redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : ''
            });
          }}>
          Enroll Now
        </EnrollNowButton>

        <ForgotUsernameLink>
          <button onClick={() => setShowForgotUsernameModal(true)}>
            Forgot your email?
          </button>
          <span className="separator">|</span>
          <button onClick={() => setShowForgotPasswordModal(true)}>
            Forgot your password?
          </button>
        </ForgotUsernameLink>

        <HelpLink>
          <button onClick={handleNeedHelp}>
            Need help?
          </button>
        </HelpLink>
      </LoginContainer>

      <SimplePasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={() => setShowPasswordChangeModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />

      <ForgotUsernameModal
        isOpen={showForgotUsernameModal}
        onClose={() => setShowForgotUsernameModal(false)}
      />

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </>
  );
}

export default SimpleLogin;
